import { useEffect, useRef } from 'react'

import { useSttStore } from 'shared/stores/sttStore'

import { isTextInsertable } from '../utils/insertAtCursor'
import { isSttPanelBlocked } from '../utils/isSttPanelBlocked'
import { SttMessageType } from '../utils/sttMessageTypes'

export const useSttFocusTracking = (isContextMenuOpen: boolean) => {
  const isFocusInsertable = useSttStore(state => state.isFocusInsertable)
  const setIsFocusInsertable = useSttStore(state => state.setIsFocusInsertable)

  const prevActiveElementRef = useRef<HTMLElement | null>(null)

  const visible = isFocusInsertable || isContextMenuOpen

  // Main document focus tracking
  useEffect(() => {
    const onFocusIn = () => {
      const el = document.activeElement
      if (isSttPanelBlocked(el)) {
        setIsFocusInsertable(false)
        return
      }
      setIsFocusInsertable(isTextInsertable(el))
    }
    const onFocusOut = () => {
      setIsFocusInsertable(false)
    }
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
    }
  }, [setIsFocusInsertable])

  // Iframe focus messages
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type !== SttMessageType.FocusChange) return
      const isInsertable = Boolean(e.data.isInsertable)
      if (isInsertable) {
        const iframe = Array.from(
          document.querySelectorAll<HTMLIFrameElement>('iframe')
        ).find(f => f.contentWindow === e.source)
        if (iframe) {
          if (isSttPanelBlocked(iframe)) return
          prevActiveElementRef.current = iframe
        }
      }
      setIsFocusInsertable(isInsertable)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [setIsFocusInsertable])

  return {
    prevActiveElementRef,
    visible
  }
}
